import { createFileRoute, Link } from "@tanstack/react-router";

import { Page, QuoteForm } from "../components/site";
import { SITE } from "../lib/seo-data";

export const Route = createFileRoute("/veelgestelde-vragen")({
  head: () => {
    const url = `${SITE}/veelgestelde-vragen`;
    return {
      meta: [
        { title: "Hoe vind je een betrouwbare elektricien? | offerto" },
        {
          name: "description",
          content:
            "Hoe herken je een betrouwbare elektricien? Welke certificeringen tellen (NEN 1010, NEN 3140, Sterkin), wat kost een elektricien per uur, welke extra kosten zijn er en waarom wit en erkend veiliger is dan zwart.",
        },
        { property: "og:title", content: "Hoe vind je een betrouwbare elektricien?" },
        {
          property: "og:description",
          content:
            "Certificeringen, reviews, uurtarieven, extra kosten en rode vlaggen: alles om een betrouwbare elektricien te kiezen zonder verrassingen.",
        },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:locale", content: "nl_NL" },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [...faq, ...faqKosten].map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
              { "@type": "ListItem", position: 2, name: "Veelgestelde vragen", item: url },
            ],
          }),
        },
      ],
    };
  },
  component: VeelgesteldeVragen,
});

const faq = [
  {
    q: "Hoe vind ik een betrouwbare elektricien?",
    a: "Vraag meerdere offertes aan, controleer of het bedrijf ingeschreven staat bij de Kamer van Koophandel en beschikt over de juiste certificeringen (NEN 1010, NEN 3140, Sterkin), lees recente reviews op onafhankelijke platforms en vraag om een schriftelijke offerte met een transparant uurtarief. Via offerto krijg je tot vijf offertes van bedrijven die deze controles al doorlopen zijn.",
  },
  {
    q: "Welke certificeringen zijn belangrijk bij een elektricien?",
    a: "De belangrijkste zijn NEN 1010 (veilig aanleggen en aansluiten van elektrische installaties in woningen), NEN 3140 (periodieke controle van elektrische apparatuur en gereedschap), Sterkin/REI (erkend voor het werken aan de netaansluiting), VEI (Veiligheid Eerste Inspectie, voorkeuring van installaties) en inschrijving in het TechniekRegister. VCA-gecertificeerde monteurs tonen aan dat ze veilig werken volgen.",
  },
  {
    q: "Mag elke elektricien aan mijn installatie werken?",
    a: "Voor werk binnen de woning geldt de NEN 1010-norm: een vakbekwame elektricien mag aan de installatie werken zolang hij of zij zich aan deze norm houdt. Voor werk aan de netaansluiting zelf — de meterkast en de koppeling met het net — is een Sterkin-erkend bedrijf verplicht. Vraag altijd na of de elektricien gecertificeerd is voor het soort werk dat jij nodig hebt.",
  },
  {
    q: "Waar herken ik een goede elektricien aan?",
    a: "Aan een KVK-inschrijving, een aansprakelijkheidsverzekering, een transparant uurtarief (zonder verborgen kosten), schriftelijke documentatie van het werk en heldere communicatie over planning en prijs. Een betrouwbare elektricien legt rustig uit wat er moet gebeuren en stuurt vooraf een offerte met de werkzaamheden, materialen en kosten.",
  },
  {
    q: "Op welke reviews let ik bij het kiezen van een elektricien?",
    a: "Let op het volume (voldoende recente reviews), de actualiteit (reviews uit de afgelopen maanden wegen zwaarder dan die van jaren geleden), de consistentie (gaat het over prijs, afspraken en vakmanschap?) en het platform (Google, Klantenvertellen of Trustpilot zijn onafhankelijker dan reviews die alleen op de eigen site staan). Eén slechte review met een goede reactie zegt meer dan alleen een hoog gemiddelde.",
  },
  {
    q: "Wat zijn rode vlaggen bij een elektricien?",
    a: "Geen vast adres of alleen een mobiel nummer, druk om direct ter plekke te beslissen, geen schriftelijke offerte, uiterst lage prijzen die niet kloppen met de markt, weigering om certificeringen te tonen, of werken zonder factuur. Wie vakmanschap levert, heeft geen moeite met een offerte op papier, een KVK-nummer en een referentie.",
  },
  {
    q: "Hoeveel offertes moet ik aanvragen?",
    a: "Vraag minimaal drie offertes aan zodat je prijs, planning en voorwaarden kunt vergelijken. Via offerto deel je één aanvraag met maximaal vijf erkende elektriciens uit jouw regio, zodat je zonder extra moeite een goed beeld krijgt van de markt en zelf kiest wie je inschakelt — of niemand.",
  },
  {
    q: "Kan ik om referenties vragen aan een elektricien?",
    a: "Ja, een betrouwbare elektricien geeft zonder probleem referenties van vergelijkbare klussen. Vraag bijvoorbeeld naar een recent project in jouw buurt of een klus van hetzelfde type (groepenkast vervangen, laadpaal installeren). Wie goed werk levert, heeft klanten die dat willen bevestigen.",
  },
];

const faqKosten = [
  {
    q: "Wat kost een elektricien per uur in 2026?",
    a: "Een zelfstandige elektricien rekent doorgaans € 55 tot € 85 per uur; grotere installatiebedrijven zitten eerder op € 75 tot € 110 per uur. Voor spoed buiten kantooruren geldt vaak een toeslag van 50 tot 100 procent. Via offerto vergelijk je de uurtarieven van meerdere elektriciens uit jouw regio naast elkaar.",
  },
  {
    q: "Wat kost een elektricien gemiddeld voor een klus?",
    a: "Dat hangt af van de klus: een kleine reparatie of een extra stopcontact zit vaak tussen € 100 en € 300, een perilex-aansluiting tussen € 150 en € 400 en een complete groepenkast tussen € 750 en € 1.800 inclusief arbeid. Op onze dienstenpagina's vind je per klus een actuele richtprijs met de kostenposten erbij.",
  },
  {
    q: "Welke extra kosten komen er bij een elektricien bovenop het uurtarief?",
    a: "De meest voorkomende extra posten zijn voorrijkosten (vaak € 25 tot € 50 of een vast starttarief), materiaalkosten, parkeerkosten in de binnenstad en een spoedtoeslag. Een betrouwbare elektricien zet deze posten vooraf in de offerte, zodat je achteraf niet voor verrassingen staat.",
  },
  {
    q: "Is zwart een elektricien inhuren goedkoper?",
    a: "Zwarte tarieven lijken lager, maar je loopt serieuze risico's: geen garantie, geen aansprakelijkheid bij schade of brand, geen geldige keuring voor je verzekering en mogelijk boetes bij loon- of btw-fraude. Een verzekeraar kan bij brandschade uitbetalen weigeren als de installatie niet volgens NEN 1010 is aangelegd. Een erkende, wit werkzame elektricien is op den duur vrijwel altijd voordeliger.",
  },
  {
    q: "Wat kost een groepenkast laten vervangen?",
    a: "Reken op € 750 tot € 1.800 inclusief materiaal en arbeid, afhankelijk van het aantal groepen, de aardlekschakelaars en of je hoofdaansluiting verzwaard moet worden. De verzwaring door de netbeheerder komt daar nog los bij. Op de dienstenpagina over de groepenkast zie je de volledige kostenopbouw en reken je jouw situatie door.",
  },
];

function VeelgesteldeVragen() {
  return (
    <Page
      title="Hoe vind ik een betrouwbare elektricien?"
      intro="Certificeringen, reviews, uurtarieven, rode vlaggen en het aantal offertes: zo kies je een elektricien die het werk veilig en transparant uitvoert, zonder verrassingen achteraf. Vergelijk gratis en vrijblijvend tot vijf bedrijven uit jouw regio."
    >
      <nav className="mt-6 text-sm text-muted-foreground" aria-label="Kruimelpad">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="px-2">/</span>
        <span className="text-foreground">Veelgestelde vragen</span>
      </nav>

      <div className="mt-6 rounded-lg border border-border bg-card p-5 sm:flex sm:items-center sm:justify-between sm:gap-6">
        <p className="text-sm text-muted-foreground">
          Vergelijk gratis en vrijblijvend tot 5 offertes van betrouwbare, erkende elektriciens bij jou in de buurt.
        </p>
        <Link
          to="/aanvraag"
          className="mt-4 inline-block rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground sm:mt-0 sm:shrink-0"
        >
          Gratis offertes aanvragen
        </Link>
      </div>

      <h2 className="mt-16 text-2xl font-extrabold tracking-tight">Betrouwbare elektricien kiezen</h2>
      <dl className="mt-6 divide-y divide-border rounded-lg border border-border bg-card">
        {faq.map((f) => (
          <div key={f.q} className="p-6">
            <dt className="font-semibold">{f.q}</dt>
            <dd className="mt-2 text-sm text-muted-foreground">{f.a}</dd>
          </div>
        ))}
      </dl>

      <h2 className="mt-16 text-2xl font-extrabold tracking-tight">Kosten en uurtarief</h2>
      <dl className="mt-6 divide-y divide-border rounded-lg border border-border bg-card">
        {faqKosten.map((f) => (
          <div key={f.q} className="p-6">
            <dt className="font-semibold">{f.q}</dt>
            <dd className="mt-2 text-sm text-muted-foreground">{f.a}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-12 grid gap-4 sm:grid-cols-2">
        <Link
          to="/diensten"
          className="rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary"
        >
          <span className="font-semibold">Bekijk diensten en richtprijzen</span>
          <p className="mt-1 text-sm text-muted-foreground">
            Groepenkast, laadpaal, perilex en meer: wat je ongeveer betaalt per klus.
          </p>
        </Link>
        <Link
          to="/hoe-het-werkt"
          className="rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary"
        >
          <span className="font-semibold">Zo werkt offertes vergelijken</span>
          <p className="mt-1 text-sm text-muted-foreground">
            In drie stappen van aanvraag naar offertes: gratis en vrijblijvend.
          </p>
        </Link>
      </div>

      <div className="mt-12">
        <QuoteForm />
      </div>
    </Page>
  );
}
